import type * as Api from '@ViewModels';

/**
 * Formats a schedule object into a human-readable string.
 *
 * This function converts an automation schedule into a descriptive text
 * that explains when the action will occur relative to the trigger.
 *
 * @param schedule - The schedule configuration object
 * @returns A formatted string describing the schedule, or 'Not scheduled' if no schedule is provided
 *
 * @example
 * ```typescript
 * formatScheduleText({ numberOfDays: 2 })
 * // Returns: "2 days after trigger"
 *
 * formatScheduleText({ numberOfDays: -1 })
 * // Returns: "1 day before trigger"
 *
 * formatScheduleText({ numberOfDays: 0 })
 * // Returns: "0 days same day as trigger"
 *
 * formatScheduleText(undefined)
 * // Returns: "Not scheduled"
 * ```
 */
export function formatScheduleText(schedule: Api.IAutomationStepSchedule | undefined | null): string {
	if (!schedule) {
		return 'Not scheduled';
	}

	const numberOfDays = schedule.numberOfDays || 0;
	const absoluteDays = Math.abs(numberOfDays);
	const dayLabel = absoluteDays !== 1 ? 'days' : 'day';

	let qualifier: string;
	if (numberOfDays < 0) {
		qualifier = 'before';
	} else if (numberOfDays > 0) {
		qualifier = 'after';
	} else {
		qualifier = 'same day as';
	}

	return `${absoluteDays} ${dayLabel} ${qualifier} trigger`;
}
